import React, { useEffect, useId } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Listbox } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";

import { setLocalities } from "@/src/store/filter";

import IconArrow from "assets/svg/chevron_right_black.svg";

import CheckBoxItem from "components/common/checkbox-item";
import Input from "components/common/input";

import styles from "./styles.module.css";

const CheckboxDropdown = ({
  placeHolder,
  options,
  buttonClass,
  listClass,
  disabled,
  handleDropdownButton,
  selectedItems = [],
  handleOptionChange,
  handleInputChange,
  handleChildOptionChange,
  inputValue,
  onOpenChange,
}) => {
  const dispatch = useDispatch();
  const localitiesRedux = useSelector((state) => state.filter.localities);

  const dropdownClass = classNames({
    [styles.dropdown]: true,
    [buttonClass]: buttonClass,
  });

  const listClasses = classNames({
    [styles.list]: true,
    [listClass]: listClass,
  });

  const id = useId();

  const displaySelectedItems = () => {
    const textLength = 15;
    if (!selectedItems || selectedItems.length === 0) {
      return <span className={styles.placeholder}>{placeHolder}</span>;
    }

    let firstItem = selectedItems[0].name;
    if (firstItem.length > textLength) {
      firstItem = `${firstItem.substring(0, textLength)}...`;
    }

    if (selectedItems.length === 1) {
      return firstItem;
    } else {
      return `${firstItem} +${selectedItems.length - 1}`;
    }
  };

  const isCheckedParent = (option) =>
    option?.childs?.every((item) => selectedItems.some((selected) => selected.id === item.id)) ||
    selectedItems.some((selected) => selected.id === option.id);
  const isCheckedChild = (childItem) => selectedItems.some((selected) => selected.id === childItem.id);

  const handleKeyDown = (event) => {
    if (event?.key === " ") {
      event.stopPropagation();
    }
  };
  const handleOption = (town, e) => {
    const values = localitiesRedux.length ? [...localitiesRedux] : [];
    if (town?.childs?.length) {
      if (values.length && values.filter((item) => item.id === town.id).length) {
        dispatch(setLocalities(values.filter((item) => item.id !== town.id)));
      } else {
        values.push(town);
        dispatch(setLocalities(values));
      }
    }
    handleOptionChange(town, e);
  };

  return (
    <Listbox value={selectedItems} multiple disabled={disabled}>
      {({ open }) => {
        useEffect(() => {
          onOpenChange(open);
        }, [open]);
        return (
          <>
            <Listbox.Button
              aria-expanded={open ? "true" : "false"}
              className={dropdownClass}
              onClick={handleDropdownButton}
            >
              <span className={styles.buttoncontent}>{displaySelectedItems()}</span>
              <span className={styles.icon}>
                <IconArrow />
              </span>
            </Listbox.Button>
            <Listbox.Options className={listClasses}>
              <div className={styles.filterInput}>
                <Input
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeHolder}
                />
              </div>
              {options.map((option, index) => (
                <React.Fragment key={`${id}+${index}`}>
                  <span className={styles.townTitle}>{option.key}</span>
                  {option.towns.map((town, townIndex) => (
                    <Listbox.Option
                      key={`${id}-${townIndex}`}
                      value={town.name}
                      className={({ active }) => `${styles.optionpadding} ${active && styles.optionselected}`}
                    >
                      <CheckBoxItem
                        title={town.name}
                        name={town?.id}
                        onChange={(e) => handleOption(town, e)}
                        checkBoxClass={town?.childs && styles.parentClass}
                        checked={isCheckedParent(town)}
                      />
                      {town?.childs &&
                        town?.childs.map((item, index) => (
                          <CheckBoxItem
                            key={`${id}+${index}`}
                            title={item?.name}
                            name={item?.id}
                            onChange={(e) => handleChildOptionChange(e, item)}
                            checkBoxClass={styles.childClass}
                            checked={isCheckedChild(item)}
                          />
                        ))}
                    </Listbox.Option>
                  ))}
                </React.Fragment>
              ))}
            </Listbox.Options>
          </>
        );
      }}
    </Listbox>
  );
};

CheckboxDropdown.propTypes = {
  options: PropTypes.array,
  placeHolder: PropTypes.string,
  buttonClass: PropTypes.any,
  listClass: PropTypes.any,
  disabled: PropTypes.bool,
  handleDropdownButton: PropTypes.func,
  selectedItems: PropTypes.array,
  handleOptionChange: PropTypes.func,
  handleInputChange: PropTypes.func,
  handleChildOptionChange: PropTypes.func,
  inputValue: PropTypes.string,
  onOpenChange: PropTypes.func,
};

export default CheckboxDropdown;
